import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';

import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';

import { SN_API_URL } from '../providers/api-url.provider';

@Injectable()
export class SnLocaleInterceptor implements HttpInterceptor {
  private readonly PSEUDOLOC_LANG = 'en-XA';
  
  constructor(
    @Inject(SN_API_URL) private readonly _apiUrl: string,
    private readonly _injector: Injector,
  ) {}

  public intercept(request: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(this._apiUrl)) {
      return handler.handle(request);
    }

    if (request.headers.has('X-User-Locale')) {
      return handler.handle(request);
    }

    const localeHeader = this._buildLocaleHeader();
    if (!localeHeader) {
      return handler.handle(request);
    }

    const modifiedRequest = request.clone({
      headers: request.headers.set('X-User-Locale', localeHeader),
    });

    return handler.handle(modifiedRequest);
  }

  private _buildLocaleHeader(): string | null {
    const languages = this._getLanguagePriority();
    
    if (languages.length === 0) {
      return null;
    }

    return languages
      .map((lang, index) => {
        if (index === 0) {
          return lang;
        }
        const qualityFactor = Math.max(0.1, 1.0 - index * 0.1).toFixed(1);
        return `${lang};q=${qualityFactor}`;
      })
      .join(', ');
  }

  private _getLanguagePriority(): string[] {
    const languages: string[] = [];

    try {
      const translocoService = this._injector.get(TranslocoService);
      const activeLang = translocoService.getActiveLang();
      
      if (activeLang && activeLang !== this.PSEUDOLOC_LANG) {
        languages.push(activeLang);
      }
    } catch (error) {
      console.warn('TranslocoService not available in LocaleInterceptor:', error);
    }

    const genericLang = this._extractGenericLanguage(languages[0]);
    if (genericLang && !languages.includes(genericLang)) {
      languages.push(genericLang);
    }

    if (!languages.includes('en-US')) {
      languages.push('en-US');
    }

    if (!languages.includes('en')) {
      languages.push('en');
    }

    return this._deduplicateLanguages(languages);
  }

  private _extractGenericLanguage(locale: string | undefined): string | null {
    if (!locale || !locale.includes('-')) {
      return null;
    }
    
    const parts = locale.split('-');
    return parts[0];
  }

  private _deduplicateLanguages(languages: string[]): string[] {
    return [...new Set(languages)];
  }
}

